import React, { useState, useEffect } from 'react';
// Framer Motion supprimé - utilisation d'animations CSS
import { 
  Search, 
  Filter, 
  Download, 
  Plus, 
  Eye, 
  Edit, 
  Trash2,
  User,
  Calendar,
  MapPin,
  Heart,
  AlertCircle
} from 'lucide-react';
import { usePatients } from '../../hooks/useApi';
import { usePatientsCrud } from '../../hooks/usePatientsCrud';
import { useNotification } from '../../hooks/useNotification';
import apiService from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import PatientDetailsModal from './PatientDetailsModal';
import PatientEditModal from './PatientEditModal';
import PatientCreateModal from './PatientCreateModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import Notification from '../common/Notification';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const PatientsList = () => {
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase();
  const [doctorId, setDoctorId] = useState(null);
  const [isLoadingDoctorId, setIsLoadingDoctorId] = useState(true);
  
  // Récupérer le userId du docteur (qui sera utilisé pour filtrer assignedDoctorId)
  useEffect(() => {
    const fetchDoctorUserId = async () => {
      if (role === 'doctor' && user?.id) {
        try {
          setIsLoadingDoctorId(true);
          // Pour les docteurs, on utilise directement le userId de l'utilisateur connecté
          // car assignedDoctorId stocke le userId du docteur, pas le doctorId
          console.log('✅ Utilisation du userId du docteur:', user.id, 'Type:', typeof user.id);
          setDoctorId(user.id); // Stocker le userId au lieu du doctorId
        } catch (err) {
          console.error('❌ Erreur lors de la récupération du userId:', err);
        } finally {
          setIsLoadingDoctorId(false);
        }
      } else {
        // Pour les admins et autres rôles, pas besoin d'attendre
        setIsLoadingDoctorId(false);
      }
    };
    fetchDoctorUserId();
  }, [role, user?.id]);

  // Utiliser le hook avec le doctorId si disponible
  // Pour les docteurs, on attend que doctorId soit chargé (nombre) avant d'appeler usePatients
  // Pour les admins, on passe null pour charger tous les patients
  // Ne jamais passer null pour les docteurs, seulement undefined (en attente) ou un nombre (valide)
  const getDoctorIdForHook = () => {
    if (role === 'doctor') {
      // Pour les docteurs, on attend que doctorId soit défini (nombre)
      if (isLoadingDoctorId) {
        return undefined; // En attente du chargement
      }
      if (doctorId === null) {
        return undefined; // Docteur non trouvé ou erreur, ne pas charger
      }
      return doctorId; // doctorId est un nombre valide
    }
    // Pour les admins et autres rôles, passer null pour charger tous les patients
    return null;
  };

  const { data: patients = [], loading, error, refetch } = usePatients(getDoctorIdForHook());
  const { createPatient, updatePatient, deletePatient, loading: crudLoading } = usePatientsCrud();
  const { notification, showSuccess, showError, hideNotification } = useNotification();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Pour les docteurs, s'assurer qu'on n'affiche rien tant que doctorId n'est pas chargé
  const basePatients = (role === 'doctor' && (isLoadingDoctorId || doctorId === null)) ? [] : patients;

  const statuses = [...new Set(basePatients.map(patient => patient.status))];

  const filteredPatients = basePatients.filter(patient => {
    const patientName = patient.user?.name || '';
    const matchesSearch = patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (patient.diagnosis || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = !filterStatus || patient.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const exportToPDF = async () => {
    try {
      const doc = new jsPDF();
      
      // En-tête
      doc.setFontSize(20);
      doc.setTextColor(40, 40, 40);
      doc.text('HealthBridge - Liste des Patients', 20, 20);
      
      // Date d'export
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Exporté le: ${new Date().toLocaleDateString('fr-FR')}`, 20, 30);
      
      // Nombre de patients
      doc.text(`Nombre de patients: ${filteredPatients.length}`, 20, 35);
      
      // Tableau
      const tableData = filteredPatients.map(patient => [
        patient.user?.name || 'N/A',
        patient.age ? `${patient.age} ans` : 'N/A',
        patient.gender === 'M' ? 'Homme' : patient.gender === 'F' ? 'Femme' : 'Autre',
        patient.diagnosis || 'En cours d\'évaluation',
        patient.status || 'N/A',
        patient.lastVisit || 'N/A'
      ]);

      autoTable(doc, {
        head: [['Nom', 'Âge', 'Genre', 'Diagnostic', 'Statut', 'Dernière visite']],
        body: tableData,
        startY: 45,
        styles: { 
          fontSize: 9,
          cellPadding: 3
        },
        headStyles: { 
          fillColor: [34, 197, 94],
          textColor: [255, 255, 255],
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252]
        },
        columnStyles: {
          0: { cellWidth: 35 }, // Nom
          1: { cellWidth: 15 }, // Âge
          2: { cellWidth: 15 }, // Genre
          3: { cellWidth: 30 }, // Diagnostic
          4: { cellWidth: 15 }, // Statut
          5: { cellWidth: 20 }  // Dernière visite
        },
        margin: { top: 45 }
      });

      // Pied de page
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        doc.text(`Page ${i} sur ${pageCount}`, 20, doc.internal.pageSize.height - 10);
        doc.text('HealthBridge - Système de Gestion Médicale', doc.internal.pageSize.width - 80, doc.internal.pageSize.height - 10);
      }

      const fileName = `liste-patients-${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
      // Enregistrer l'activité de téléchargement
      try {
        await apiService.logDownload('Liste des patients', fileName);
      } catch (err) {
        console.error('Erreur lors de l\'enregistrement du téléchargement:', err);
      }
      
      // Notification de succès
      showSuccess('Export PDF', 'Liste des patients exportée avec succès');
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
      showError('Erreur', 'Erreur lors de l\'export PDF');
    }
  };

  // Fonctions pour les actions
  const handleViewPatient = (patient) => {
    setSelectedPatient(patient);
    setShowDetailsModal(true);
  };

  const handleEditPatient = (patient) => {
    setSelectedPatient(patient);
    setShowEditModal(true);
  };

  const handleCreatePatient = () => {
    setShowCreateModal(true);
  };

  const handleSaveNewPatient = async (formData) => {
    try {
      // Filtrer les champs qui ne sont pas dans le modèle backend
      const patientData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        age: parseInt(formData.age),
        gender: formData.gender,
        bloodType: formData.bloodType || null,
        emergencyContact: formData.emergencyContact || null,
        emergencyPhone: formData.emergencyPhone || null,
        medicalHistory: formData.medicalHistory || null,
        allergies: formData.allergies || null,
        currentMedications: formData.currentMedications || null,
        insuranceNumber: formData.insuranceNumber || null
      };
      
      const createResult = await createPatient(patientData);
      
      // Rafraîchir la liste
      await refetch();
      
      setShowCreateModal(false);
      
      // Notification de succès
      showSuccess('Succès', 'Patient créé avec succès');
    } catch (error) {
      console.error('Erreur lors de la création:', error);
      showError('Erreur', error.message || 'Erreur lors de la création du patient');
    }
  };

  const handleSavePatient = async (formData) => {
    if (!selectedPatient) return;
    
    try {
      await updatePatient(selectedPatient.id, formData);
      
      // Rafraîchir la liste
      refetch();
      
      setShowEditModal(false);
      setSelectedPatient(null);
      
      // Notification de succès
      showSuccess('Succès', 'Patient modifié avec succès');
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      showError('Erreur', 'Erreur lors de la modification du patient');
    }
  };

  const handleDeletePatient = (patient) => {
    setSelectedPatient(patient);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedPatient) return;
    
    try {
      await deletePatient(selectedPatient.id);
      
      // Rafraîchir la liste
      refetch();
      
      setShowDeleteModal(false);
      setSelectedPatient(null);
      
      // Notification de succès
      showSuccess('Succès', 'Patient supprimé avec succès');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      showError('Erreur', 'Erreur lors de la suppression du patient');
    }
  };

  const closeModals = () => {
    setShowDetailsModal(false);
    setShowEditModal(false);
    setShowCreateModal(false);
    setShowDeleteModal(false);
    setSelectedPatient(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'En traitement':
        return 'bg-yellow-100 text-yellow-800';
      case 'Stable':
        return 'bg-green-100 text-green-800';
      case 'Critique':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'En traitement':
        return <Heart className="h-4 w-4" />;
      case 'Stable':
        return <User className="h-4 w-4" />;
      case 'Critique':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className=" px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Gestion des Patients</h1>
              <p className="text-gray-600 mt-2">Suivez et gérez les dossiers des patients</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={exportToPDF}
                className="bg-secondary-600 hover:bg-secondary-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200"
              >
                <Download className="h-4 w-4" />
                <span>Exporter PDF</span>
              </button>
              <button
                onClick={handleCreatePatient}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200"
              >
                <Plus className="h-4 w-4" />
                <span>Nouveau Patient</span>
              </button>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {(loading || (role === 'doctor' && isLoadingDoctorId)) && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Chargement des patients...</p>
          </div>
        )}

        {/* Error State */}
        {(error || (role === 'doctor' && !isLoadingDoctorId && doctorId === null)) && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Erreur de chargement</h3>
                <p className="text-sm text-red-700 mt-1">
                  {error || (role === 'doctor' && doctorId === null ? 'Impossible de trouver votre profil docteur. Veuillez contacter l\'administrateur.' : error)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filter */}
        {!loading && !error && !(role === 'doctor' && isLoadingDoctorId) && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Rechercher un patient..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Tous les statuts</option>
                {statuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
            <div className="text-sm text-gray-500 flex items-center">
              {filteredPatients.length} patient(s) trouvé(s)
            </div>
          </div>
        </div>
        )}

        {/* Patients Table */}
        {!loading && !error && !(role === 'doctor' && isLoadingDoctorId) && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Patient
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Âge/Genre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Diagnostic
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dernière visite
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPatients.map((patient, index) => (
                  <tr
                    key={patient.id}
                    className="hover:bg-gray-50 transition-colors duration-200"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <User className="h-5 w-5 text-primary-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{patient.user?.name || 'Patient inconnu'}</div>
                          <div className="text-sm text-gray-500">{patient.user?.phone || 'N/A'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {patient.age} ans / {patient.gender === 'M' ? 'Homme' : patient.gender === 'F' ? 'Femme' : 'Autre'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {patient.diagnosis || 'En cours d\'évaluation'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(patient.status)}`}>
                        {getStatusIcon(patient.status)}
                        <span className="ml-1">{patient.status}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                        {patient.lastVisit}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewPatient(patient)}
                          className="text-primary-600 hover:text-primary-900"
                          title="Voir les détails"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditPatient(patient)}
                          className="text-secondary-600 hover:text-secondary-900"
                          title="Modifier"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeletePatient(patient)}
                          className="text-red-600 hover:text-red-900"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {filteredPatients.length === 0 && (
          <div className="text-center py-12">
            <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun patient trouvé</h3>
            <p className="text-gray-500">Essayez de modifier vos critères de recherche</p>
          </div>
        )}
      </div>

      {/* Modals */}
      <PatientDetailsModal
        patient={selectedPatient}
        isOpen={showDetailsModal}
        onClose={closeModals}
      />
      
      <PatientEditModal
        patient={selectedPatient}
        isOpen={showEditModal}
        onClose={closeModals}
        onSave={handleSavePatient}
        loading={crudLoading}
      />
      
      <PatientCreateModal
        isOpen={showCreateModal}
        onClose={closeModals}
        onSave={handleSaveNewPatient}
        loading={crudLoading}
      />
      
      <DeleteConfirmModal
        patient={selectedPatient}
        isOpen={showDeleteModal}
        onClose={closeModals}
        onConfirm={confirmDelete}
        loading={crudLoading}
      />

      {/* Notification */}
      <Notification
        isVisible={notification.isVisible}
        onClose={hideNotification}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        duration={notification.duration}
      />
    </div>
  );
};

export default PatientsList;
